import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useEffect, useMemo, useRef } from 'react';
import * as yup from 'yup';
import { useForm, type Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ControlledField } from '../ControlledField';
import { filenameStringSchema } from '@shared/schemas';
import { ButtonLoading } from '../loading/ButtonLoading';

type Mode = 'input' | 'number';
type ValueOf<M extends Mode> = M extends 'number' ? number : string;
type SchemaOf<M extends Mode> = M extends 'number' ? yup.NumberSchema<number> : yup.StringSchema<string>;
type FormData<M extends Mode> = { text: ValueOf<M> };

export type TSingleFieldDialogState<M extends Mode = 'input'> = {
  isOpen: boolean;
  inputType?: M;
  defaultText: ValueOf<M>;
  title: string;
  label: string;
  buttonLabel?: string;
  afterLabel?: string;
  schema?: SchemaOf<M>;
  onSubmit: (data: { text: ValueOf<M> }) => void | Promise<void>;
};

type TProps<M extends Mode = 'input'> = {
  state?: TSingleFieldDialogState<M>;
  setState: (state: TSingleFieldDialogState<M>) => void;
};

const defaultStringSchema = filenameStringSchema();

const defaultNumberSchema: yup.NumberSchema<number> = yup
  .number()
  .transform((val, orig) => (typeof orig === 'string' && orig.trim() === '' ? NaN : Number(orig)))
  .typeError('Please enter a number')
  .integer('Must be an integer')
  .min(1, 'Must be at least 1')
  .required('Required');

const SingleFieldDialog = <M extends Mode = 'input'>({ state, setState }: TProps<M>) => {
  const mode: Mode = state?.inputType ?? 'input';
  const isNumber = mode === 'number';

  // 1) Resolve single-field schema
  const fieldSchema = useMemo<SchemaOf<M>>(
    () => (state?.schema ?? (isNumber ? defaultNumberSchema : defaultStringSchema)) as SchemaOf<M>,
    [state?.schema, isNumber]
  );

  // 2) Yup object schema - keep it as AnyObjectSchema to avoid TS conflicts across yup versions
  const objectSchema: yup.AnyObjectSchema = useMemo(
    () => yup.object({ text: fieldSchema }) as yup.AnyObjectSchema,
    [fieldSchema]
  );

  // 3) Resolver typed once to our FormData<M>
  const resolver = useMemo(() => yupResolver(objectSchema) as unknown as Resolver<FormData<M>>, [objectSchema]);

  // 4) RHF form with strong typing
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData<M>>({
    resolver,
    defaultValues: { text: isNumber ? (1 as unknown as ValueOf<M>) : ('' as unknown as ValueOf<M>) },
  });

  const refField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (state?.isOpen) {
      const dv = (state.defaultText ?? (isNumber ? 1 : '')) as ValueOf<M>;
      const coerced = (isNumber ? Number(dv as any) : String(dv as any)) as ValueOf<M>;
      reset({ text: coerced });
      refField.current?.focus();
    }
  }, [state?.isOpen, isNumber, state?.defaultText]);

  const onSubmit = (data: FormData<M>) => state?.onSubmit({ text: data.text });

  return (
    <Dialog
      open={state?.isOpen || false}
      onOpenChange={(_, data) => {
        if (state && !isSubmitting) setState({ ...state, isOpen: data.open });
      }}
    >
      <DialogSurface>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} disabled={isSubmitting} />
                </DialogTrigger>
              }
            >
              {state?.title || ''}
            </DialogTitle>
            <DialogContent>
              <ControlledField
                inputType={mode}
                label={state?.label || ''}
                contentAfter={state?.afterLabel && <p>{state.afterLabel}</p>}
                name="text"
                control={control}
                errors={errors}
                ref={refField}
                disabled={isSubmitting}
              />
            </DialogContent>
            <DialogActions>
              <ButtonLoading type="submit" appearance="primary" loading={isSubmitting}>
                {state?.buttonLabel || 'OK'}
              </ButtonLoading>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" disabled={isSubmitting}>
                  Cancel
                </Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
};

export { SingleFieldDialog };
